/*
 * Copyright (c) Dito Technologies LLC
 */

import React from "react";
import {createFragmentContainer} from "react-relay";
import {graphql} from "babel-plugin-relay/macro";
import styles from "ui/feature/piece-play/common/piece-renderer/PieceRendererStepThruNote.module.scss";
import {
    PieceRendererStepThruNote_note$data
} from "ui/feature/piece-play/common/__generated__/PieceRendererStepThruNote_note.graphql";
import classnames from "classnames";
import {NoteDisplayState} from "ui/feature/piece-play/common/PlayTypes";

interface Props {
    note: PieceRendererStepThruNote_note$data;
    maxTime: number;
    maxMidi: number;
    minMidi: number;
    noteState: NoteDisplayState;
}

const PieceRendererStepThruNote: React.FC<Props> = ({note, maxMidi, minMidi, maxTime, noteState}) => {

    const topPercent = React.useMemo(() => {
        const totalMidi = Math.abs(maxMidi - minMidi) + 1;
        const currMidi = note.midi_number - minMidi;
        const percent = (currMidi / totalMidi);
        return `${(1 - percent) * 100}%`;
    }, [maxMidi, minMidi, note.midi_number]);

    const leftPercent = React.useMemo(() => {
        return `${(note.note_start / maxTime) * 100}%`
    }, [maxTime, note.note_start]);

    const widthPercent = React.useMemo(() => {
        return `${(note.note_duration / maxTime) * 100}%`;
    }, [maxTime, note.note_duration]);

    const heightPercent = React.useMemo(() => {
        const totalMidi = Math.abs(maxMidi - minMidi) + 1;
        return `${(1 / totalMidi) * 100}%`
    }, [maxMidi, minMidi])

    return (
        <div className={classnames(styles.container, {
            [styles.success]: noteState === "success",
            [styles.error]: noteState === "error",
            [styles.current]: noteState === "current",
            [styles.none]: noteState === "none",
        })} style={{
            left: leftPercent,
            top: topPercent,
            width: widthPercent,
            height: heightPercent
        }}/>
    );
}

export default createFragmentContainer(PieceRendererStepThruNote, {
    note: graphql`
    fragment PieceRendererStepThruNote_note on MusicPieceFileNote {
        bulb_id
        note_duration
        note_start
        midi_number
    }
    `
});
